import React from 'react';
import clsx from 'clsx';

import { Color } from 'types/colors';
import { SpriteIcon } from 'types/icons';

import Icons from './sprite-icons.svg';

import css from './Icon.module.scss';

// Icons are vector images that have a single fill color in Figma. They can be any size, but are
// generally square.
//
// Adding or updating an icon:
// - Export svg from Figma to src/assets/sprite-icons
//      - Use the component name from there, e.g. "Icon/airplane" => "airplane")
//      - Icons stored in that directory are used to generate the sprite, not referenced directly
// - Run `yarn make-icon-sprite`. This generates sprite-icons.svg.
// - Add icon name to ICONS below
const ICONS = [
    'airplane',
    'apple-circle-filled',
    'arrow-down-thick',
    'arrow-down',
    'arrow-left-thick',
    'arrow-left-wide-thin',
    'arrow-right-thick',
    'arrow-right-wide-thin',
    'arrow-up-thick',
    'arrow-up',
    'bell-alert',
    'birthday-cake-circle-filled',
    'birthday-cake-solid',
    'birthday-cake',
    'blocked',
    'book',
    'bookmark',
    'briefcase-thin',
    'briefcase',
    'calendar',
    'camera-circle-filled',
    'camera-plus',
    'camera',
    'chart',
    'chat-bubble-circle-filled',
    'chat-bubble-exclamation',
    'chat-bubble-filled',
    'chat-bubble-outline',
    'chat-bubble-small-filled',
    'chat-bubbles',
    'check-circle-filled',
    'check-circle',
    'check-pointed-small',
    'check-small-circle-filled',
    'check-thin',
    'check',
    'clock-filled',
    'clock-outline',
    'cloud-up-arrow',
    'copy',
    'correct-text',
    'crop',
    'cross-circle-filled-thick',
    'cross-circle-filled',
    'cross-circle-thin',
    'cross-thick',
    'cross',
    'distance',
    'dots-horizontal',
    'dots-vertical',
    'download',
    'envelope',
    'exclamation-circle-filled',
    'external-link',
    'eye',
    'facebook-circle-filled',
    'filters-thin',
    'filters',
    'flag',
    'flower',
    'food-drink',
    'footprints',
    'games',
    'gif',
    'gift-circle-filled',
    'gift',
    'glasses',
    'globe',
    'google-circle-filled',
    'graduate-cap',
    'graduation-cap-thin',
    'graph',
    'guitar',
    'heart-circle-filled',
    'heart-cross',
    'heart-filled',
    'heart-outline-thin',
    'heart-outline',
    'home',
    'images',
    'instagram-circle-filled',
    'instagram',
    'interpals-about',
    'interpals-logo-slogan',
    'interpals-logo',
    'interpals-text',
    'interpals',
    'keyboard',
    'language-advanced',
    'language-beginner',
    'language-fluent',
    'language-intermediate',
    'language-native',
    'list',
    'location-pin',
    'lock',
    'logout',
    'meet-up',
    'menu',
    'microphone',
    'music',
    'notebook',
    'paperclip',
    'pause-circle-filled',
    'pencil-app',
    'pencil-filled',
    'pencil',
    'phone-circle-filled',
    'pin',
    'play-circle-filled',
    'playback-progress',
    'plus-circle-filled',
    'plus-small-circle-filled',
    'plus-thick',
    'plus',
    'post',
    'question',
    'refresh',
    'refresh-double-arrows',
    'reply-circle-filled',
    'reply',
    'report-circle-thin',
    'report-circle',
    'robot',
    'search-input',
    'search-long',
    'search',
    'send-plane',
    'settings-gear',
    'sex-both',
    'sex-female-thin',
    'sex-female',
    'sex-male-thin',
    'sex-male',
    'share',
    'signup-birthday',
    'signup-email',
    'signup-home',
    'signup-location',
    'signup-password',
    'signup-person',
    'signup-sex',
    'smile',
    'sport',
    'star',
    'stop-circle-filled',
    'stories',
    'support-headphones',
    'support-lifesaver',
    'switch',
    'translate',
    'trash-2',
    'trash',
    'trash-circle-filled',
    'tv',
    'twitter-circle-filled',
    'upload',
    'user-circle-filled',
    'user-frame',
    'user',
    'users-three-circle',
    'users-three',
    'users-two',
    'verification',
    'vk-circle-filled',
    'youtube-circle-filled',
    'moderation-flagged',
    'moderation-list',
    'moderation-log',
    'moderation-newest',
    'moderation-reports',
    'moderation-user-search',
] as const;

interface Props {
    name: SpriteIcon;
    width: number;
    // Height is only for non-square icons
    height?: number;
    color?: Color;
    className?: string;
}

const Icon: React.FC<Props> = ({ name, width, height, color, className }) => {
    const rootVars = getComputedStyle(document.body);

    return (
        // Without this div specifying a width/height, we're seeing 2px bottom margin occurring despite
        // width/height being specified on the svg itself (see https://imgur.com/a/QeVoitA)
        // Todo: Why is this happening?
        <div
            style={{
                width: width,
                height: height ?? width,
            }}
            className={clsx(className)}
        >
            <svg
                className={css.icon}
                color={color ? rootVars.getPropertyValue(`--${color}`) : 'inherit'}
                width={width}
                height={height ?? width}
            >
                <use xlinkHref={`${Icons}#${name}`} />
            </svg>
        </div>
    );
};

export { ICONS };

export default React.memo(Icon);
